import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CheckCircleIcon, UserIcon, EnvelopeIcon, CalendarIcon, GiftIcon } from '@heroicons/react/24/solid';

export default function PurchasedItemsPage() {
    const { token } = useAuth();
    const [purchasedCampaigns, setPurchasedCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchPurchasedCampaigns = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/purchased`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Could not fetch purchased campaigns.');
            const data = await response.json();
            setPurchasedCampaigns(data);
        } catch (error) {
            console.error(error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { 
        fetchPurchasedCampaigns(); 
    }, [fetchPurchasedCampaigns]);

    if (loading) return (
        <div className="flex justify-center items-center min-h-64">
            <p className="text-lg text-gray-600 dark:text-gray-300">Loading purchased items...</p>
        </div>
    );

    if (error) return (
        <div className="flex justify-center items-center min-h-64">
            <p className="text-lg text-red-500">Error: {error}</p>
        </div>
    );

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Purchased Items</h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                        View all successfully purchased campaigns and their details
                    </p>
                </div>
                <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-4 py-2 rounded-lg flex items-center">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    <span className="font-semibold">{purchasedCampaigns.length} Purchased Items</span>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Product</th>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Recipient</th>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Donor</th>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Purchase Date</th>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Emails</th>
                                <th className="p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                            {purchasedCampaigns.length > 0 ? (
                                purchasedCampaigns.map((campaign) => (
                                    <PurchasedItemRow key={campaign.id} campaign={campaign} />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex flex-col items-center justify-center">
                                            <GiftIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                                            <p className="text-lg font-medium">No purchased items found</p>
                                            <p className="text-sm">When campaigns are purchased, they will appear here.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// Sub-component for each purchased item row
const PurchasedItemRow = ({ campaign }) => {
    const [showDetails, setShowDetails] = useState(false);

    return (
        <>
            <tr 
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                onClick={() => setShowDetails(!showDetails)}
            >
                <td className="p-4">
                    <div className="flex items-center space-x-3">
                        <GiftIcon className="w-8 h-8 text-orange-500" />
                        <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                                {campaign.product_name}
                            </div>
                            {campaign.product_price && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    ₦{campaign.product_price}
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td className="p-4">
                    <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-white">
                            {campaign.recipient_first_name} {campaign.recipient_last_name}
                        </span>
                    </div>
                </td>
                <td className="p-4">
                    <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4 text-blue-400" />
                        <span className="text-gray-900 dark:text-white">
                            {campaign.donor_first_name} {campaign.donor_last_name}
                        </span>
                    </div>
                </td>
                <td className="p-4">
                    <div className="flex items-center space-x-2">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">
                            {new Date(campaign.purchased_at).toLocaleDateString()}
                        </span>
                    </div>
                </td>
                <td className="p-4">
                    <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                            <EnvelopeIcon className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {campaign.recipient_email}
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <EnvelopeIcon className="w-4 h-4 text-blue-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                {campaign.donor_email}
                            </span>
                        </div>
                    </div>
                </td>
                <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        campaign.delivery_status === 'delivered' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}>
                        {campaign.delivery_status || 'pending'}
                    </span>
                </td>
            </tr>
            {showDetails && (
                <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <td colSpan="6" className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                            <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                                    <UserIcon className="w-4 h-4 mr-2 text-green-500" />
                                    Recipient Details
                                </h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Name:</span> {campaign.recipient_first_name} {campaign.recipient_last_name}</p>
                                    <p><span className="font-medium">Email:</span> {campaign.recipient_email}</p>
                                    {campaign.recipient_phone && (
                                        <p><span className="font-medium">Phone:</span> {campaign.recipient_phone}</p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                                    <UserIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    Donor Details
                                </h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Name:</span> {campaign.donor_first_name} {campaign.donor_last_name}</p>
                                    <p><span className="font-medium">Email:</span> {campaign.donor_email}</p>
                                    {campaign.donor_phone && (
                                        <p><span className="font-medium">Phone:</span> {campaign.donor_phone}</p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                                    <GiftIcon className="w-4 h-4 mr-2 text-orange-500" />
                                    Product Details
                                </h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Product:</span> {campaign.product_name}</p>
                                    {campaign.product_price && (
                                        <p><span className="font-medium">Price:</span> ₦{campaign.product_price}</p>
                                    )}
                                    <p><span className="font-medium">Purchase Date:</span> {new Date(campaign.purchased_at).toLocaleString()}</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                                    <CheckCircleIcon className="w-4 h-4 mr-2 text-purple-500" />
                                    Delivery Status
                                </h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Status:</span> {campaign.delivery_status || 'pending'}</p>
                                    {campaign.appreciation_video_url && (
                                        <p>
                                            <span className="font-medium">Appreciation Video:</span>{' '}
                                            <a href={campaign.appreciation_video_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                                View Video
                                            </a>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}